import { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import { watchMembership } from "./lib/membership";
import { watchLicense } from "./lib/billing";
import AuthScreen from "./screens/AuthScreen";
import FacilityAdminDashboard from "./screens/FacilityAdminDashboard";
import VendorAdminDashboard from "./screens/VendorAdminDashboard";
import PaywallScreen from "./screens/PaywallScreen";
import FieldFlow from "./screens/FieldFlow";
import StaffHome from "./screens/StaffHome";
import StaffAuthScreen, { getStaffSession } from "./screens/StaffAuthScreen";
import { S, C } from "./ui";

// Phase 1 routing: Firebase Auth state decides logged-in vs. not;
// memberships/{uid} (see src/lib/membership.js and firestore.rules)
// decides which role's dashboard to show. No client-side role storage
// anywhere else — this document is the only source of truth.
export default function App() {
  const [user, setUser] = useState(undefined); // undefined = not loaded yet
  const [membership, setMembership] = useState(undefined);
  const [license, setLicense] = useState(undefined); // undefined = not loaded yet, null = never subscribed
  // Phase 11: a logged-in staff session lives in localStorage, not
  // Firebase Auth (see StaffAuthScreen.jsx for why) — read once at
  // mount, and updated the moment StaffAuthScreen calls onStaffLogin so
  // the app switches to StaffHome without a full page reload.
  const [staffSession, setStaffSession] = useState(getStaffSession);
  // Set the instant "Staff" is clicked on AuthScreen — before
  // StaffAuthScreen's anonymous sign-in even runs. Without this, the
  // anonymous user it creates would flip `user` truthy and fall into the
  // membership-resolution logic below (an anonymous session has no
  // memberships/{uid} doc, landing on "Account not fully set up") — the
  // exact same reason isFieldFlow below is checked unconditionally,
  // before any user/membership state, rather than nested inside
  // AuthScreen. staffMode short-circuits the same way for the Staff path.
  const [staffMode, setStaffMode] = useState(false);

  // Phase 2: a QR-scanned asset bypasses the admin auth flow entirely —
  // matches CustodialView's own isCheckin pattern (?cid=&area=) — field
  // workers never sign in with email/password, see FieldFlow.jsx.
  const params = new URLSearchParams(window.location.search);
  const fieldFacilityId = params.get("facilityId");
  const fieldAssetId = params.get("assetId");
  const isFieldFlow = !!(fieldFacilityId && fieldAssetId);

  useEffect(() => onAuthStateChanged(auth, setUser), []);

  useEffect(() => {
    if (!user) { setMembership(user === null ? null : undefined); return; }
    return watchMembership(user.uid, setMembership);
  }, [user]);

  // Billing (Phase 6): only a Facility Admin is a paying customer — Vendor
  // Admins are invited in free by the facility they're scoped to, so this
  // never runs for them. Keyed by the admin's own uid, same as
  // licenses/{ownerUid} — see functions/index.js's header comment.
  useEffect(() => {
    if (!user || membership?.role !== "facility_admin") { setLicense(undefined); return; }
    return watchLicense(user.uid, setLicense);
  }, [user, membership]);

  // A QR scan is always the more specific intent — honor it even if this
  // device also has a cached general Staff session (e.g. staff logged in
  // via the home screen earlier, then scans a code for one exact asset).
  if (isFieldFlow) return <FieldFlow facilityId={fieldFacilityId} assetId={fieldAssetId} />;

  if (staffSession) return <StaffHome facilityId={staffSession.facilityId} workerId={staffSession.workerId} />;
  if (staffMode) return <StaffAuthScreen onLogin={setStaffSession} />;

  if (user === undefined) {
    return <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted }}>Loading…</div>;
  }

  if (!user) return <AuthScreen onStaffMode={() => setStaffMode(true)} />;

  if (membership === undefined) {
    return <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted }}>Loading…</div>;
  }

  if (membership === null) {
    // Authenticated but no membership doc — shouldn't normally happen
    // (AuthScreen creates both in one batch), but a partial failure or a
    // manually-created auth account could land here.
    return (
      <div style={{ ...S.center }}>
        <div style={{ ...S.card, textAlign: "center" }}>
          <div style={S.title}>Account not fully set up</div>
          <div style={S.sub}>Your sign-in worked, but no facility or vendor is linked to this account yet. Contact your facility admin, or sign out and try again.</div>
          <button style={S.btn(C.navy)} onClick={() => auth.signOut()}>Sign Out</button>
        </div>
      </div>
    );
  }

  if (membership.role === "facility_admin") {
    if (license === undefined) {
      return <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted }}>Loading…</div>;
    }
    // "past_due" (a failed card charge, still retrying) keeps access during
    // Stripe's dunning grace period rather than locking the admin out
    // immediately — only "canceled" or never-subscribed hard-paywalls.
    const hasAccess = license && (license.status === "active" || license.status === "past_due");
    if (!hasAccess) return <PaywallScreen uid={user.uid} canceled={!!license && license.status === "canceled"} />;
    return <FacilityAdminDashboard membership={membership} />;
  }
  if (membership.role === "vendor_admin") return <VendorAdminDashboard membership={membership} />;

  return null;
}
